import Vue from 'vue'
import _ from 'lodash'
import { fetchEntity } from '@/api/fetcher'
import { workspaceFor } from '@/helpers/links'
import { createStatus } from '@/helpers/entities'
import {
  canonicalizeLink,
  canonicalizeStatus
} from '@/helpers/canonicalization'

import {
  ENTITY,
  STORE_ENTITY,
  BIND,
  BIND_ENTITY,
  // LOCK_REQUEST,
  // UNLOCK_REQUEST,
  DISCARD_ENTITY,
  PAPERS_GETTER,
} from '@/store/mutation-types'

/**
 * Recarrega a página a partir da origem.
 */
export function reload() {
  this.$router.go()
}

/**
 * Monta os parâmetros de navegação da rota indicada.
 *
 * @param {Keep.Paper.Design.Link | String} link
 *    Referência de requisição da entidade destino.
 * @param {Object} opts
 *    Opções de modificação da requisição.
 *    Opções disponíveis:
 *    -  site
 *    -  form
 *    -  data
 *    -  noCache
 * @param {Object} context
 *    O contexto do Vue contendo pelo menos as propriedades `$route` e `$store`.
 *    Se omitido `this` é usado como contexto.
 */
export function to(link, opts, context) {
  context ||= this
  const params = canonicalizeLink(context, link, opts)
  const target = {
    name: 'Workspace',
    params
  }
  return target
}

/**
 * Navega para a rota indicada.
 *
 * @param {Keep.Paper.Design.Link | String} link
 *    Referência de requisição da entidade destino.
 * @param {Object} opts
 *    Opções de modificação da requisição.
 *    Opções disponíveis:
 *    -  site
 *    -  form
 *    -  data
 *    -  noCache
 * @param {Object} context
 *    O contexto do Vue contendo pelo menos as propriedades `$route` e `$store`.
 *    Se omitido `this` é usado como contexto.
 */
export function go(link, opts, context) {

  if (link.href) {
    // Abrir o link em uma nova aba do browser cujo target é o próprio href.
    window.open(link.href, { target: link.href })
    return
  }

  context ||= this
  const router = context.$router
  const target = to(link, opts, context)

  // TODO: Implementar corretamente esta ligacao...
  // const rule = link.rule || []
  // const bindable = rule.some(x => x.toLowerCase() === 'refresh')
  // if (bindable) {
  //   const path = router.currentRoute.path
  //   console.log({target, path})
  //   _bind(target, path, context)
  // }

  router.push(target)
}

/**
 * Fecha um paper removendo-o do VueStore.
 *
 * @param {Keep.Paper.Design.Paper} linkOrPaper
 *    Instância do paper a ser fechado ou sua referência de entidade.
 * @param {Object} opts
 *    Opções de modificação da requisição.
 *    Opções disponíveis:
 *    -  (sem opções no momento)
 * @param {Object} context
 *    O contexto do Vue contendo pelo menos as propriedades `$route` e `$store`.
 *    Se omitido `this` é usado como contexto.
 */
export function close(linkOrPaper, opts, context) {
  context ||= this

  const { eref, site } = canonicalizeLink(context, linkOrPaper, opts)

  if (eref === 'Index.Workspace' && site === 'Home')
    return

  const store = context.$store
  const { eref: routeEref, site: routeSite } = this.$route.params

  let redirect = null

  if (eref === routeEref && site === routeSite) {
    const papers = store.getters[PAPERS_GETTER]
    const currentPaper = papers.filter(
      paper => paper.eref === eref && paper.site === site
        )[0]

    const index = papers.indexOf(currentPaper)
    const nextPaper = papers[index + 1] || papers[index - 1]

    redirect = nextPaper
      ? this.$to({ eref: nextPaper.eref, site })
      : this.$to({ eref: workspaceFor(eref), site })

    if (redirect.params.eref === eref) {
      if (redirect.params.eref === 'Index.Workspace') {
        redirect = this.$to({ eref: 'Index.Workspace', site: 'Home' })
      } else {
        redirect = this.$to({ eref: 'Index.Workspace', site })
      }
    }
  }

  store.commit(DISCARD_ENTITY, { eref, site })

  if (redirect) {
    this.$router.push(redirect)
  }
}

/**
 * Navega para o link indicado aplicando regras de navegação.
 *
 * @param {Keep.Paper.Design.Link} link
 *    Referência de requisição da entidade destino.
 * @param {Object} opts
 *    Opções de modificação da requisição.
 *    Opções disponíveis:
 *    -  site
 *    -  form
 *    -  data
 *    -  noCache
 * @param {Object} context
 *    O contexto do Vue contendo pelo menos as propriedades `$route` e `$store`.
 *    Se omitido `this` é usado como contexto.
 */
export async function browse(link, opts, context) {
  context ||= this
  link = canonicalizeLink(context, link, opts)

  // TODO: Implementar corretamente esta ligacao...
  // const rule = link.rule || []
  // const bindable = rule.some(x => x.toLowerCase() === 'refresh')
  // if (bindable) {
  //   const path = router.currentRoute.path
  //   console.log({target, path})
  //   _bind(target, path, context)
  // }

  const { $router: router } = context
  const { eref, site } = link

  // ? Quais regras se aplicam nesse momento?
  // // const rule = link.rule || []
  // // const refresh = !!rule.filter(term => term === 'Refresh').length

  // TODO: O que fazer?
  // // if (status.code === 401) {
  // //   // * Usuário não autenticado.
  // //   this.$router.push({
  // //     name: 'Workspace', params: {
  // //       site,
  // //       eref: 'Index.Login',
  // //       redirect: eref
  // //     }
  // //   })
  // //   return
  // // }
  router
    .push({ name: 'Workspace', params: { site, eref, noFetch: true } })
    .then(async () => await resolve(link, opts, context))
}

/**
 * Obtém a entidade do cache ou do servidor.
 *
 * @param {Keep.Paper.Design.Link} link
 *    Referência de requisição entidade procurada.
 * @param {Object} opts
 *    Opções de modificação da requisição.
 *    Opções disponíveis:
 *    -  site
 *    -  form
 *    -  data
 *    -  noCache
 * @param {Object} context
 *    O contexto do Vue contendo pelo menos as propriedades `$route` e `$store`.
 *    Se omitido `this` é usado como contexto.
 */
export async function resolve(link, opts, context) {

  if (link.eref === 'reload' || link.eref === 'Meta.Reload') {
    location.reload(true);
    return {
      ok: true,
      code: 200
    }
  }

  try {
    context ||= this
    opts ||= {}

    link = canonicalizeLink(context, link, opts)

    const { noCache, credential } = opts

    const store = context.$store
    const parent = context.paper || context.entity

    const path = link.path
    const rule = link.rule || []


    // console.log({ _request: path })

    // Checando a possibilidade de usarmos a versão em cache...
    if (!noCache) {
      let target = store.state[ENTITY][path]
      if (target?.keepOpen === true && target?.noCache !== true && target?.type !== 'Status') {

        // Temos a entidade em cache e autorização para usá-lo...
        return canonicalizeStatus(context, link, {
          ok: true,
          code: 200,
          entity: target
        })
      }
    }

    const status = await _request(context, link, credential)

    if (status.entity) {
      store.commit(STORE_ENTITY, status.entity);
    }

    return status

  } catch (error) {
    return createStatus(context, error, link,
      700, "A resposta do servidor não era a esperada.")
  }
}

/**
 * Realiza o download ou restauração de uma entidade.
 *
 * @link {Keep.Paper.Design.Eref | Keep.Paper.Design.Link}
 *    Link ou referência da entidade alvo.
 */
async function _request(context, link, credential) {
  const eref = link.eref
  const site = link.site
  const form = link.form
  const data = link.data

  // Destacando os parâmetros da requisição...
  const affectedData = Array.isArray(data) ? data : [data]
  const payload = { chunks: [{ form, affectedData }] }

  // Não temos a entidade em cache... Obtêmo-ei-la...
  const status = await fetchEntity(site, eref, payload, credential)

  // Padronizando referências
  canonicalizeStatus(context, link, status)

  return status
}

/**
 * Conecta entidades para producao de efeitos em cascata.
 * @param {Keep.Paper.Design.Entity} entity
 *    Entidade observada.
 * @param {Keep.Paper.Design.Entity} observer
 *    Entidade observadora.
 *    Ações executadas an entidade observada são propagadas para as entidades
 *    observdores.
 * @param {Object} context
 *    O contexto do Vue contendo pelo menos as propriedades `$route` e `$store`.
 *    Se omitido `this` é usado como contexto.
 */
function _bind(entity, observer, context) {
  if (!entity || !observer) return

  const store = context.$store
  store.commit(BIND_ENTITY, { entity, observer });
}
